import { toBool, toFiniteCount, toFiniteNumber, formatExportTimestamp, getItemExplanation } from './formatters';

export const toMergeCount = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.trunc(parsed));
};

export const toMergeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toTimeMs = (value) => {
  if (!value) return null;
  const normalized = (() => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/[zZ]$/.test(text) || /[+-]\d{2}:\d{2}$/.test(text)) return text;
    return `${text}Z`;
  })();
  const parsed = new Date(normalized).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const ACTIVE_RUN_RETENTION_GRACE_MS = 20 * 1000;
const TERMINAL_ACTIVE_RUN_PHASES = new Set([
  'completed',
  'failed',
  'error',
  'terminated',
  'cancelled',
  'canceled',
  'finished',
  'done',
]);

const isTerminalActiveRunPhase = (phase) => TERMINAL_ACTIVE_RUN_PHASES.has(String(phase || '').trim().toLowerCase());

const shouldRetainMissingActiveRun = (run, nowMs = Date.now()) => {
  if (!run || typeof run !== 'object') return false;
  if (isTerminalActiveRunPhase(run?.phase)) return false;
  const updatedAtMs = toTimeMs(run?.updated_at);
  if (updatedAtMs === null) return false;
  const ageMs = Math.max(0, nowMs - updatedAtMs);
  return ageMs <= ACTIVE_RUN_RETENTION_GRACE_MS;
};

export const normalizeRunConfig = (context = {}, fallback = {}, defaults = {}) => {
  const promptTemplate = context?.prompt_template || fallback?.prompt_template || '';
  const inferredEbayFromPrompt = String(promptTemplate).includes('Additional context (retrieved from eBay image search):');
  const inferredNotesFromPrompt = String(promptTemplate).includes('Additional context (few-shot examples from reviewed test-rig notes):');

  return {
    run_id: String(context?.run_id || fallback?.run_id || ''),
    pid: context?.pid ?? fallback?.pid ?? null,
    pgid: context?.pgid ?? fallback?.pgid ?? null,
    started_at: context?.started_at || fallback?.started_at || Date.now(),
    updated_at: context?.updated_at || context?.progress?.updated_at || fallback?.updated_at || null,
    model: context?.model || fallback?.model || defaults.modelName || '',
    resize_mode: context?.resize_mode || fallback?.resize_mode || defaults.resizeMode || '',
    compression_mode: context?.compression_mode || fallback?.compression_mode || defaults.compressionMode || '',
    dataset_prefix: context?.dataset_prefix || fallback?.dataset_prefix || '',
    dataset_name: context?.dataset_name || fallback?.dataset_name || defaults.datasetName || 'N/A',
    data_points: context?.data_points ?? context?.total_data_points ?? context?.progress?.total_data_points ?? fallback?.data_points ?? defaults.dataPoints ?? '-',
    total_data_points: context?.total_data_points ?? context?.progress?.total_data_points ?? fallback?.total_data_points ?? null,
    attempted_data_points: context?.attempted_data_points ?? context?.progress?.attempted_data_points ?? fallback?.attempted_data_points ?? null,
    successful_data_points: context?.successful_data_points ?? context?.progress?.successful_data_points ?? fallback?.successful_data_points ?? null,
    skipped_data_points: context?.skipped_data_points ?? context?.progress?.skipped_data_points ?? fallback?.skipped_data_points ?? null,
    progress_percent: context?.progress_percent ?? context?.progress?.progress_percent ?? fallback?.progress_percent ?? null,
    avg_duration_seconds: context?.avg_duration_seconds ?? context?.progress?.avg_duration_seconds ?? fallback?.avg_duration_seconds ?? null,
    eta_seconds: context?.eta_seconds ?? context?.progress?.eta_seconds ?? fallback?.eta_seconds ?? null,
    current_lot: context?.current_lot ?? context?.progress?.current_lot ?? fallback?.current_lot ?? null,
    current_index: context?.current_index ?? context?.progress?.current_index ?? fallback?.current_index ?? null,
    current_attempt: context?.current_attempt ?? context?.progress?.current_attempt ?? fallback?.current_attempt ?? null,
    last_error: context?.last_error ?? context?.progress?.last_error ?? fallback?.last_error ?? '',
    dataset_truncated: toBool(context?.dataset_truncated, toBool(fallback?.dataset_truncated, false)),
    phase: context?.phase || context?.progress?.phase || fallback?.phase || 'running',
    prompt_template: promptTemplate,
    use_ebay_rag: toBool(
      context?.use_ebay_rag,
      toBool(fallback?.use_ebay_rag, inferredEbayFromPrompt || defaults.useEbayRag || false),
    ),
    use_notes_rag: toBool(
      context?.use_notes_rag,
      toBool(fallback?.use_notes_rag, inferredNotesFromPrompt || defaults.useNotesRag || false),
    ),
  };
};

export const normalizeActiveRuns = (contexts = [], defaults = {}) => {
  const deduped = [];
  const seenKeys = new Set();

  contexts.forEach((ctx) => {
    if (!ctx || typeof ctx !== 'object') return;
    const normalized = normalizeRunConfig(ctx, {}, defaults);
    const key = normalized.run_id || String(normalized.pid || '');
    if (key && seenKeys.has(key)) return;
    if (key) seenKeys.add(key);
    deduped.push(normalized);
  });

  deduped.sort((a, b) => {
    const ta = new Date(a.started_at).getTime();
    const tb = new Date(b.started_at).getTime();
    const safeA = Number.isFinite(ta) ? ta : 0;
    const safeB = Number.isFinite(tb) ? tb : 0;
    return safeB - safeA;
  });

  return deduped;
};

export const mergeActiveRunForward = (previousRun, nextRun) => {
  if (!previousRun) return nextRun;
  if (!nextRun) return previousRun;

  const previousUpdatedAt = toTimeMs(previousRun?.updated_at);
  const nextUpdatedAt = toTimeMs(nextRun?.updated_at);
  const preferNextSnapshot = (
    nextUpdatedAt !== null
      && (previousUpdatedAt === null || nextUpdatedAt >= previousUpdatedAt)
  );

  const merged = { ...previousRun, ...nextRun };
  const previousTotal = toMergeCount(previousRun?.total_data_points ?? previousRun?.data_points);
  const nextTotal = toMergeCount(nextRun?.total_data_points ?? nextRun?.data_points);
  const resolvedTotal = (
    nextTotal !== null && nextTotal > 0
      ? nextTotal
      : (previousTotal !== null && previousTotal > 0 ? previousTotal : nextTotal)
  );
  merged.total_data_points = resolvedTotal;
  if (resolvedTotal !== null) {
    merged.data_points = resolvedTotal;
  }

  ['attempted_data_points', 'successful_data_points', 'skipped_data_points', 'current_index', 'current_attempt']
    .forEach((field) => {
      const previousValue = toMergeCount(previousRun?.[field]);
      const nextValue = toMergeCount(nextRun?.[field]);
      if (previousValue === null && nextValue === null) return;
      if (preferNextSnapshot && nextValue !== null) {
        merged[field] = nextValue;
        return;
      }
      if (nextValue === null) {
        merged[field] = previousValue;
        return;
      }
      if (previousValue === null) {
        merged[field] = nextValue;
        return;
      }
      merged[field] = Math.max(previousValue, nextValue);
    });

  const previousProgress = toMergeNumber(previousRun?.progress_percent);
  const nextProgress = toMergeNumber(nextRun?.progress_percent);
  if (previousProgress !== null || nextProgress !== null) {
    if (preferNextSnapshot && nextProgress !== null) {
      merged.progress_percent = nextProgress;
    } else if (nextProgress === null) {
      merged.progress_percent = previousProgress;
    } else if (previousProgress === null) {
      merged.progress_percent = nextProgress;
    } else {
      merged.progress_percent = Math.max(previousProgress, nextProgress);
    }
  }

  const previousEta = toMergeNumber(previousRun?.eta_seconds);
  const nextEta = toMergeNumber(nextRun?.eta_seconds);
  if (nextEta !== null || previousEta !== null) {
    merged.eta_seconds = (nextEta !== null ? nextEta : previousEta);
  }

  const previousAvgDuration = toMergeNumber(previousRun?.avg_duration_seconds);
  const nextAvgDuration = toMergeNumber(nextRun?.avg_duration_seconds);
  if (nextAvgDuration !== null || previousAvgDuration !== null) {
    merged.avg_duration_seconds = (nextAvgDuration !== null ? nextAvgDuration : previousAvgDuration);
  }

  if (previousUpdatedAt !== null && nextUpdatedAt !== null) {
    merged.updated_at = (nextUpdatedAt >= previousUpdatedAt) ? nextRun?.updated_at : previousRun?.updated_at;
  } else {
    merged.updated_at = nextRun?.updated_at || previousRun?.updated_at || null;
  }

  merged.started_at = nextRun?.started_at || previousRun?.started_at || Date.now();
  merged.phase = nextRun?.phase || previousRun?.phase || 'running';
  merged.last_error = nextRun?.last_error || previousRun?.last_error || '';
  return merged;
};

export const mergeActiveRunsForward = (previousRuns, nextRuns, defaults = {}) => {
  const nowMs = Date.now();
  const previousByKey = new Map();
  (previousRuns || []).forEach((run) => {
    const key = String(run?.run_id || run?.pid || '').trim();
    if (key) previousByKey.set(key, run);
  });

  const merged = (nextRuns || []).map((run) => {
    const key = String(run?.run_id || run?.pid || '').trim();
    if (!key) return run;
    return mergeActiveRunForward(previousByKey.get(key), run);
  });

  const nextKeys = new Set(
    (nextRuns || [])
      .map((run) => String(run?.run_id || run?.pid || '').trim())
      .filter(Boolean)
  );

  (previousRuns || []).forEach((run) => {
    const key = String(run?.run_id || run?.pid || '').trim();
    if (!key || nextKeys.has(key)) return;
    if (!shouldRetainMissingActiveRun(run, nowMs)) return;
    merged.push(run);
  });

  return normalizeActiveRuns(merged, defaults);
};

export const getResultTokenCounts = (result = {}) => {
  const usage = result && typeof result.usage === 'object' ? result.usage : {};
  const input = toFiniteCount(
    result?.input_tokens_consumed ??
    result?.prompt_tokens ??
    usage?.prompt_tokens ??
    usage?.input_tokens
  );
  const output = toFiniteCount(
    result?.output_tokens_consumed ??
    result?.completion_tokens ??
    usage?.completion_tokens ??
    usage?.output_tokens
  );
  const total = toFiniteCount(
    result?.total_tokens_consumed ??
    result?.total_tokens ??
    usage?.total_tokens
  );
  const hasAnyToken = input !== null || output !== null || total !== null;
  if (!hasAnyToken) {
    return { input: null, output: null, total: null };
  }
  if ((input ?? 0) === 0 && (output ?? 0) === 0 && (total ?? 0) === 0) {
    return { input: null, output: null, total: null };
  }
  const resolvedTotal = (
    total !== null
      ? total
      : (input !== null && output !== null ? (input + output) : null)
  );
  return { input, output, total: resolvedTotal };
};

const normalizeResultSource = (sourceValue, fallbackCacheHit = false, options = {}) => {
  const allowUnknown = options?.allowUnknown === true;
  const rawSource = String(sourceValue || '').trim().toLowerCase();
  if (rawSource === 'cache' || rawSource === 'provider') {
    return rawSource;
  }
  if (allowUnknown && rawSource === 'unknown') {
    return 'unknown';
  }
  return fallbackCacheHit ? 'cache' : 'provider';
};

export const getSourceDisplayLabel = (sourceValue) => {
  if (sourceValue === 'cache') return 'CACHE';
  if (sourceValue === 'provider') return 'LIVE';
  return 'UNKNOWN';
};

const parseOptionalBoolFlag = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  return toBool(value, false);
};

const REVIEWER_NOTE_SOURCES = new Set(['reviewer', 'human', 'manual', 'user']);
const normalizeNoteCompareText = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

export const getTrustedReviewNote = (item = {}) => {
  if (!item || typeof item !== 'object') return '';
  const reviewNote = typeof item.review_note === 'string' ? item.review_note : '';
  const normalizedNote = normalizeNoteCompareText(reviewNote);
  if (!normalizedNote) return '';

  const noteSource = normalizeNoteCompareText(item.review_note_source);
  return REVIEWER_NOTE_SOURCES.has(noteSource) ? reviewNote : '';
};

export const getResultCacheInfo = (result = {}) => {
  const cacheHit = toBool(result?.cache_hit, false);
  const normalizedSource = normalizeResultSource(result?.analysis_source, cacheHit);
  const cacheKey = typeof result?.cache_key === 'string' ? result.cache_key.trim() : '';
  return {
    cacheHit: normalizedSource === 'cache' || cacheHit,
    source: normalizedSource,
    cacheKey,
  };
};

export const getResultAttemptSourceInfo = (result = {}) => {
  const cacheInfo = getResultCacheInfo(result);
  const explicitFinalCacheHit = parseOptionalBoolFlag(result?.final_attempt_cache_hit);
  const explicitFirstCacheHit = parseOptionalBoolFlag(result?.first_attempt_cache_hit);
  const finalCacheHit = explicitFinalCacheHit !== null ? explicitFinalCacheHit : cacheInfo.cacheHit;
  const firstCacheHit = explicitFirstCacheHit !== null ? explicitFirstCacheHit : finalCacheHit;
  const explicitFinalSource = typeof result?.final_attempt_source === 'string'
    ? result.final_attempt_source.trim()
    : '';
  const explicitFirstSource = typeof result?.first_attempt_source === 'string'
    ? result.first_attempt_source.trim()
    : '';
  const finalSource = explicitFinalSource
    ? normalizeResultSource(explicitFinalSource, finalCacheHit, { allowUnknown: true })
    : normalizeResultSource(result?.analysis_source, finalCacheHit, { allowUnknown: true });
  const firstSource = explicitFirstSource
    ? normalizeResultSource(explicitFirstSource, firstCacheHit, { allowUnknown: true })
    : finalSource;
  return {
    firstSource,
    finalSource,
    firstCacheHit,
    finalCacheHit,
    cacheKey: cacheInfo.cacheKey,
  };
};

export const getRunCacheStats = (run = {}) => {
  const runResults = Array.isArray(run?.results) ? run.results : [];
  const explicitCacheHitCount = toFiniteCount(run?.cache_hit_count);
  const computedCacheHitCount = runResults.reduce((sum, resultEntry) => {
    const cacheInfo = getResultCacheInfo(resultEntry);
    return cacheInfo.cacheHit ? (sum + 1) : sum;
  }, 0);
  const count = explicitCacheHitCount !== null ? explicitCacheHitCount : computedCacheHitCount;
  const total = runResults.length;
  const explicitRate = toFiniteNumber(run?.cache_hit_rate);
  const rate = (
    explicitRate !== null
      ? Math.max(0, Math.min(1, explicitRate))
      : (total > 0 ? (count / total) : 0)
  );
  return { count, total, rate };
};

export const getRunTokenTotals = (run = {}) => {
  const runInput = toFiniteCount(run?.input_tokens_consumed);
  const runOutput = toFiniteCount(run?.output_tokens_consumed);
  const runTotal = toFiniteCount(run?.total_tokens_consumed);
  const hasRunTokenField = runInput !== null || runOutput !== null || runTotal !== null;
  const hasRunTokenValue = (runInput ?? 0) > 0 || (runOutput ?? 0) > 0 || (runTotal ?? 0) > 0;
  if (hasRunTokenField && hasRunTokenValue) {
    const total = (
      runTotal !== null
        ? runTotal
        : (runInput !== null && runOutput !== null ? (runInput + runOutput) : null)
    );
    return { input: runInput, output: runOutput, total };
  }

  const runResults = Array.isArray(run?.results) ? run.results : [];
  let hasInput = false;
  let hasOutput = false;
  let hasTotal = false;
  let input = 0;
  let output = 0;
  let total = 0;
  runResults.forEach((result) => {
    const counts = getResultTokenCounts(result);
    if (counts.input !== null) {
      hasInput = true;
      input += counts.input;
    }
    if (counts.output !== null) {
      hasOutput = true;
      output += counts.output;
    }
    if (counts.total !== null) {
      hasTotal = true;
      total += counts.total;
    }
  });
  if (!hasInput && !hasOutput && !hasTotal) {
    return { input: null, output: null, total: null };
  }
  if (input <= 0 && output <= 0 && total <= 0) {
    return { input: null, output: null, total: null };
  }
  if (!hasTotal && hasInput && hasOutput) {
    total = input + output;
    hasTotal = true;
  }
  return {
    input: hasInput ? input : null,
    output: hasOutput ? output : null,
    total: hasTotal ? total : null,
  };
};

export const getRunProgressMetrics = (run = {}) => {
  const rawTotal = toFiniteCount(run?.total_data_points ?? run?.data_points);
  const total = (rawTotal !== null && rawTotal > 0) ? rawTotal : null;
  let successful = toFiniteCount(run?.successful_data_points);
  let skipped = toFiniteCount(run?.skipped_data_points);
  let attempted = toFiniteCount(run?.attempted_data_points);
  if (successful === null) successful = 0;
  if (skipped === null) skipped = 0;
  if (attempted === null) attempted = successful + skipped;
  if (total !== null) attempted = Math.min(total, attempted);

  const explicitPercent = toFiniteNumber(run?.progress_percent);
  const progressPercent = explicitPercent !== null
    ? Math.max(0, Math.min(100, explicitPercent))
    : (
      total && total > 0
        ? Math.max(0, Math.min(100, (attempted / total) * 100))
        : 0
    );
  const avgDurationSeconds = toFiniteNumber(run?.avg_duration_seconds);
  const etaSeconds = toFiniteNumber(run?.eta_seconds);
  return {
    total,
    attempted,
    successful,
    skipped,
    progressPercent,
    avgDurationSeconds,
    etaSeconds,
    currentLot: run?.current_lot,
    currentIndex: toFiniteCount(run?.current_index),
    currentAttempt: toFiniteCount(run?.current_attempt),
    phase: run?.phase || 'running',
    lastError: run?.last_error || '',
    updatedAt: run?.updated_at || null,
    datasetTruncated: toBool(run?.dataset_truncated, false),
  };
};

export const getRunPointStats = (run = {}) => {
  const attempted = toFiniteCount(
    run?.attempted_data_points ?? run?.data_points ?? run?.iterations ?? run?.num_iterations
  );
  const successful = toFiniteCount(
    run?.successful_data_points ?? (Array.isArray(run?.results) ? run.results.length : null)
  );
  const explicitSkipped = toFiniteCount(run?.skipped_data_points);
  const inferredSkipped = (
    attempted !== null && successful !== null
      ? Math.max(0, attempted - successful)
      : null
  );
  const skipped = explicitSkipped !== null ? explicitSkipped : (inferredSkipped !== null ? inferredSkipped : 0);
  return { attempted, successful, skipped };
};

export const formatRunDataPoints = (run = {}) => {
  const { attempted, successful, skipped } = getRunPointStats(run);
  const truncSuffix = run?.dataset_truncated ? ' (truncated)' : '';
  if (attempted !== null && successful !== null) {
    return skipped > 0 ? `${successful}/${attempted}${truncSuffix}` : `${attempted}${truncSuffix}`;
  }
  if (attempted !== null) return `${attempted}${truncSuffix}`;
  if (successful !== null) return `${successful}${truncSuffix}`;
  return '-';
};

export const getRunDataPointsHint = (run = {}) => {
  const { attempted, successful, skipped } = getRunPointStats(run);
  if (attempted !== null && successful !== null) {
    return skipped > 0
      ? `Successful ${successful} of ${attempted} (${skipped} skipped)`
      : `Successful ${successful} of ${attempted}`;
  }
  if (attempted !== null) return `Data points: ${attempted}`;
  if (successful !== null) return `Data points: ${successful}`;
  return '';
};

export const EBAY_RAG_PROMPT_MARKER = 'Additional context (retrieved from eBay image search):';
export const EBAY_RAG_PROMPT_SUFFIX_PREVIEW = (
  'Additional context (retrieved from eBay image search):\n' +
  '[{"title":"example title","price":"0","currency":"USD","condition":"example condition","itemWebUrl":"https://example.com","itemId":"example-id"}]\n' +
  'Use this only as supporting market context. Prioritize visual evidence in the uploaded images and do not copy titles blindly if they do not match.'
);
export const NOTES_RAG_PROMPT_MARKER = 'Additional context (few-shot examples from reviewed test-rig notes):';
export const NOTES_RAG_PROMPT_SUFFIX_PREVIEW = (
  'Additional context (few-shot examples from reviewed test-rig notes):\n' +
  '[{"lot_number":"123","dataset_prefix":"Auction 18","lot_note":"Short reviewer note for the lot.","ai_estimated_total":245.5,"actual_total":210.0,"lot_accuracy":0.856,"lot_duration_ms":2180.0,"lot_cost":0.0241,"lot_cache_hit":false,"ai_items":[{"item":"Example item","estimated_value":145.5,"actual_price":120.0,"accuracy":0.825,"condition":"Used","price_source":"model_knowledge"}],"subitem_notes":[{"item":"Example item","estimated_value":145.5,"actual_price":120.0,"accuracy":0.825,"condition":"Used","price_source":"model_knowledge","review_note":"Short reviewer note for this subitem."}],"example_image_refs":["test-rig-items/dataset/lot123/img1.jpg","test-rig-items/dataset/lot123/img2.jpg"],"reference_image_metadata":[{"image_ref":"test-rig-items/dataset/lot123/img1.jpg","image_order":1,"lot_number":"123","dataset_prefix":"Auction 18","run_id":"run-abc","subitem_note_count":1,"has_lot_note":true}],"example_image_count":2}]\n' +
  'Treat these as reviewer-authored few-shot examples from prior runs. Use them as guidance when similar patterns appear, but prioritize the uploaded images and do not copy notes blindly. Use existing reviewer notes only as context; never invent, populate, or return item/subitem note fields in detected_items output.'
);
const RAG_PROMPT_MARKERS = [EBAY_RAG_PROMPT_MARKER, NOTES_RAG_PROMPT_MARKER];

export const stripEbayRagPromptBlock = (promptTemplate) => {
  const text = String(promptTemplate || '');
  const markerIndexes = RAG_PROMPT_MARKERS
    .map((marker) => text.indexOf(marker))
    .filter((idx) => idx >= 0);
  if (markerIndexes.length === 0) return text.trim();
  return text.slice(0, Math.min(...markerIndexes)).trim();
};

export const withRagPromptPreview = (promptTemplate, ebayEnabled, notesEnabled) => {
  const base = stripEbayRagPromptBlock(promptTemplate);
  const previewBlocks = [];
  if (toBool(ebayEnabled, false)) {
    previewBlocks.push(EBAY_RAG_PROMPT_SUFFIX_PREVIEW);
  }
  if (toBool(notesEnabled, false)) {
    previewBlocks.push(NOTES_RAG_PROMPT_SUFFIX_PREVIEW);
  }
  if (!base && previewBlocks.length === 0) return '';
  if (!base) return previewBlocks.join('\n\n');
  if (previewBlocks.length === 0) return base;
  return `${base}\n\n${previewBlocks.join('\n\n')}`;
};

export const withEbayRagPromptPreview = (promptTemplate, ragEnabled) => (
  withRagPromptPreview(promptTemplate, ragEnabled, false)
);

export const computeAccuracyRatio = (actualValue, estimateValue) => {
  const actual = toFiniteNumber(actualValue);
  const estimate = toFiniteNumber(estimateValue);
  if (actual === null) return null;
  const safeActual = actual;
  const safeEstimate = estimate ?? 0;
  if (safeActual === 0 && safeEstimate === 0) return 1;
  if (safeActual === 0 || safeEstimate === 0) return 0;
  return Math.min(safeActual, safeEstimate) / Math.max(safeActual, safeEstimate);
};

export const uniqueNonEmptyUrls = (values = []) => {
  const seen = new Set();
  const unique = [];
  values.forEach((url) => {
    if (typeof url !== 'string') return;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    unique.push(trimmed);
  });
  return unique;
};

export const normalizeImageKey = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  try {
    const parsed = new URL(trimmed);
    const host = String(parsed.hostname || '').toLowerCase();
    let decodedPath = String(parsed.pathname || '');
    try {
      decodedPath = decodeURIComponent(decodedPath);
    } catch (_) {
      // Keep the original encoded pathname if decode fails.
    }
    const path = decodedPath.replace(/^\/+/, '');
    if (host.includes('.blob.core.windows.net') && path.includes('/')) {
      return path.toLowerCase();
    }
    return `${host}${parsed.pathname || ''}`.toLowerCase();
  } catch (_) {
    const withoutQuery = trimmed.split('?')[0];
    try {
      return decodeURIComponent(withoutQuery).toLowerCase();
    } catch (_) {
      return withoutQuery.toLowerCase();
    }
  }
};

export const getResultLotNumber = (result = {}, item = null) => {
  const candidates = [
    item?.['lot#'],
    result?.['lot#'],
    Array.isArray(result?.items) ? result.items[0]?.['lot#'] : null,
  ];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    const normalized = String(candidate).trim();
    if (normalized) return normalized;
  }
  return '';
};

export const getResultImageUrls = (result = {}, item = null) => {
  const resultImageUrls = Array.isArray(result?.image_urls)
    ? result.image_urls
    : (typeof result?.image_urls === 'string' && result.image_urls.trim() ? [result.image_urls] : []);
  const singleResultImageUrl = typeof result?.image_url === 'string' && result.image_url.trim()
    ? [result.image_url]
    : [];
  const itemImageUrls = Array.isArray(item?.image_urls)
    ? item.image_urls
    : (typeof item?.image_urls === 'string' && item.image_urls.trim() ? [item.image_urls] : []);
  const singleItemImageUrl = typeof item?.image_url === 'string' && item.image_url.trim()
    ? [item.image_url]
    : [];
  return uniqueNonEmptyUrls([
    ...resultImageUrls,
    ...singleResultImageUrl,
    ...itemImageUrls,
    ...singleItemImageUrl,
  ]);
};

export const getDisabledImageKeySet = (result = {}) => {
  const disabled = new Set();
  const disabledKeys = Array.isArray(result?.disabled_image_keys) ? result.disabled_image_keys : [];
  const disabledUrls = Array.isArray(result?.disabled_image_urls) ? result.disabled_image_urls : [];
  [...disabledKeys, ...disabledUrls].forEach((entry) => {
    const normalized = normalizeImageKey(entry);
    if (normalized) disabled.add(normalized);
  });
  return disabled;
};

export const getLotActionKey = (runId, lotNumber) => {
  const runPart = String(runId || '').trim() || 'no-run';
  const lotPart = String(lotNumber || '').trim() || 'no-lot';
  return `${runPart}::${lotPart}`;
};

export const getImageActionKey = (runId, lotNumber, imageUrl) => {
  const lotKey = getLotActionKey(runId, lotNumber);
  const imageKey = normalizeImageKey(imageUrl) || String(imageUrl || '').trim() || 'no-image';
  return `${lotKey}::${imageKey}`;
};

export const getReviewNoteDraftKey = ({ runId, lotNumber, resultIndex, itemIndex, historyIndex }) => {
  const runPart = String(runId || '').trim() || 'no-run';
  const lotPart = String(lotNumber || '').trim() || 'no-lot';
  const resultPart = Number.isFinite(Number(resultIndex)) ? String(Number(resultIndex)) : 'no-result';
  const itemPart = Number.isFinite(Number(itemIndex)) ? String(Number(itemIndex)) : 'no-item';
  const histPart = historyIndex != null && Number.isFinite(Number(historyIndex)) ? `::hist-${Number(historyIndex)}` : '';
  return `${runPart}::${lotPart}::${resultPart}::${itemPart}${histPart}`;
};

export const escapeCsvCell = (value) => {
  let text = '';
  if (value !== null && value !== undefined) {
    if (typeof value === 'object') {
      try {
        text = JSON.stringify(value);
      } catch (_) {
        text = String(value);
      }
    } else {
      text = String(value);
    }
  }
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export const toSafeFilenamePart = (value, fallback = '') => {
  const normalized = String(value || '')
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
};

export const buildRunExportRows = (run = {}) => {
  const runResults = Array.isArray(run?.results) ? run.results : [];
  const runTimestamp = formatExportTimestamp(run?.timestamp);
  const rows = [];

  runResults.forEach((result, resultIndex) => {
    const resultItems = Array.isArray(result?.items) && result.items.length > 0 ? result.items : [null];
    const resultImageUrls = Array.isArray(result?.image_urls)
      ? result.image_urls
      : (typeof result?.image_urls === 'string' && result.image_urls.trim() ? [result.image_urls] : []);
    const lotNumber = result?.['lot#'] ?? '';
    const cacheInfo = getResultCacheInfo(result);
    const attemptSourceInfo = getResultAttemptSourceInfo(result);
    const durationFromAnalysis = toFiniteNumber(result?.analysis_duration_ms);
    const durationSeconds = toFiniteNumber(result?.duration);
    const durationMs = durationFromAnalysis !== null
      ? Number(durationFromAnalysis.toFixed(0))
      : (durationSeconds === null ? '' : Number((durationSeconds * 1000).toFixed(0)));
    const cost = toFiniteNumber(result?.analysis_cost ?? result?.cost);
    const resultTokenTotals = getResultTokenCounts(result);
    const estimatedSum = Array.isArray(result?.items)
      ? result.items.reduce((sum, entry) => sum + (toFiniteNumber(entry?.estimated) || 0), 0)
      : 0;
    const lotActual = Array.isArray(result?.items)
      ? result.items.map((entry) => toFiniteNumber(entry?.actual)).find((value) => value !== null)
      : null;
    const lotAccuracy = computeAccuracyRatio(lotActual, estimatedSum);

    resultItems.forEach((item, itemIndex) => {
      const itemImageUrls = Array.isArray(item?.image_urls)
        ? item.image_urls
        : (typeof item?.image_urls === 'string' && item.image_urls.trim() ? [item.image_urls] : []);
      const singleItemImageUrl = typeof item?.image_url === 'string' && item.image_url.trim()
        ? [item.image_url]
        : [];
      const mergedImageUrls = uniqueNonEmptyUrls([...resultImageUrls, ...itemImageUrls, ...singleItemImageUrl]);

      const actual = toFiniteNumber(item?.actual);
      const estimated = toFiniteNumber(item?.estimated);
      const itemAccuracy = computeAccuracyRatio(actual, estimated);
      const explanation = getItemExplanation(item);

      rows.push({
        run_timestamp: runTimestamp,
        run_id: run?.run_id || '',
        dataset_name: run?.dataset_name || '',
        dataset_prefix: run?.dataset_prefix || '',
        model: run?.model || '',
        mode: run?.mode || '',
        resize_mode: run?.resize_mode || '',
        compression_mode: run?.compression_mode || '',
        use_ebay_rag: toBool(run?.use_ebay_rag, false) ? 'true' : 'false',
        use_notes_rag: toBool(run?.use_notes_rag, false) ? 'true' : 'false',
        lot_number: item?.['lot#'] ?? lotNumber,
        result_index: resultIndex,
        item_index: item ? itemIndex : '',
        item_name: item?.item || item?.item_name || '',
        actual_price: actual === null ? '' : actual,
        estimated_price: estimated === null ? '' : estimated,
        ai_estimated_sum: estimatedSum,
        item_accuracy_percent: itemAccuracy === null ? '' : Number((itemAccuracy * 100).toFixed(2)),
        lot_accuracy_percent: lotAccuracy === null ? '' : Number((lotAccuracy * 100).toFixed(2)),
        explanation: explanation || '',
        review_note: item?.review_note || '',
        duration_ms: durationMs,
        cost_usd: cost === null ? '' : Number(cost.toFixed(6)),
        first_attempt_source: attemptSourceInfo.firstSource,
        final_attempt_source: attemptSourceInfo.finalSource,
        first_attempt_cache_hit: attemptSourceInfo.firstCacheHit === null
          ? ''
          : (attemptSourceInfo.firstCacheHit ? 'true' : 'false'),
        final_attempt_cache_hit: attemptSourceInfo.finalCacheHit === null
          ? ''
          : (attemptSourceInfo.finalCacheHit ? 'true' : 'false'),
        analysis_source: cacheInfo.source,
        cache_hit: cacheInfo.cacheHit ? 'true' : 'false',
        cache_key: cacheInfo.cacheKey || '',
        input_tokens_consumed: resultTokenTotals.input,
        output_tokens_consumed: resultTokenTotals.output,
        total_tokens_consumed: resultTokenTotals.total,
        image_urls: mergedImageUrls.join(' | '),
        bounding_box: item?.bounding_box || '',
        raw_item: item || '',
      });
    });
  });

  return rows;
};

export const CSV_EXPORT_HEADERS = [
  'run_timestamp',
  'run_id',
  'dataset_name',
  'dataset_prefix',
  'model',
  'mode',
  'resize_mode',
  'compression_mode',
  'use_ebay_rag',
  'use_notes_rag',
  'lot_number',
  'result_index',
  'item_index',
  'item_name',
  'actual_price',
  'estimated_price',
  'ai_estimated_sum',
  'item_accuracy_percent',
  'lot_accuracy_percent',
  'explanation',
  'review_note',
  'duration_ms',
  'cost_usd',
  'first_attempt_source',
  'final_attempt_source',
  'first_attempt_cache_hit',
  'final_attempt_cache_hit',
  'analysis_source',
  'cache_hit',
  'cache_key',
  'input_tokens_consumed',
  'output_tokens_consumed',
  'total_tokens_consumed',
  'image_urls',
  'bounding_box',
  'raw_item',
];

export const exportRunToCsv = (run = {}, displayedIndex = 0) => {
  const rows = buildRunExportRows(run);
  if (rows.length === 0) {
    alert('No detailed row results found for this test run.');
    return;
  }

  const csvLines = [
    CSV_EXPORT_HEADERS.join(','),
    ...rows.map((row) => CSV_EXPORT_HEADERS.map((header) => escapeCsvCell(row[header])).join(',')),
  ];
  const blob = new Blob([csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = URL.createObjectURL(blob);

  const runPart = toSafeFilenamePart(run?.run_id, `run-${displayedIndex + 1}`);
  const timestampPart = toSafeFilenamePart(formatExportTimestamp(run?.timestamp).replace(/[:.]/g, '-'), 'timestamp');
  const fileName = `test-run-${runPart}-${timestampPart}.csv`;

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};
